import { game } from '../Game';
import { Settings } from '../Settings';

// Hitstop: the game loop skips whole updates while frames are held, so the
// simulation, level time and replays never change. Each held frame is still
// drawn, with display-only offsets that are put back right after the draw:
// the victim shakes (sideways while it stands on something, any direction in
// the air) with an amplitude that dies out over the hold, the attacker shakes
// a little, and everything that moves creeps on by part of one frame's motion.

export const HITSTOP_FRAMES = {
	EYE: 3,
	IMPACT: 3,
	LIMB: 4,
	LIMB_BASH: 5,
	HEAD: 6,
	HEAD_BASH: 7,
};

const COOLDOWN_FRAMES = 30;
const SCREEN_MARGIN = 100;
// Shake per held frame of the request, in screen pixels, so it reads the same at any zoom.
const SHAKE_PER_FRAME = 1.5;
const ATTACKER_SHAKE = 0.3;
// Total creep over the whole hold, as a share of one frame's motion.
const CREEP = 0.5;

const { getPointer, NULL } = Box2D;

let holdFrames = 0;
let totalFrames = 0;
let cooldown = 0;
let granted = 0;
let amplitude = 0;
let sideways = false;
let screenShake = false;
const shaken = new Map();

const touchingGround = bodies => bodies.some(body => {
	if (!body || body.destroyed) return false;
	for (let edge = body.GetContactList(); getPointer(edge) !== getPointer(NULL); edge = edge.get_next()) {
		const contact = edge.contact;
		if (contact.IsTouching() && edge.other.GetType() === Box2D.b2_staticBody
			&& !contact.GetFixtureA().IsSensor() && !contact.GetFixtureB().IsSensor()) return true;
	}
	return false;
});

// Point is in world units. During the cooldown a bigger hit only adds the
// difference, so one crash that snaps a limb and then a head holds once.
export const requestHitstop = (frames, point, { victim = [], attacker = null, screen = false } = {}) => {
	const extra = cooldown > 0 ? frames - granted : frames;
	if (extra <= 0) return false;
	const position = game.editor.container.toGlobal({ x: point.x * Settings.PTM, y: point.y * Settings.PTM });
	if (position.x < -SCREEN_MARGIN || position.x > window.innerWidth + SCREEN_MARGIN
		|| position.y < -SCREEN_MARGIN || position.y > window.innerHeight + SCREEN_MARGIN) return false;

	if (holdFrames <= 0) {
		shaken.clear();
		screenShake = false;
		amplitude = 0;
	}
	holdFrames += extra;
	totalFrames = holdFrames;
	granted = Math.max(granted, frames);
	cooldown = COOLDOWN_FRAMES;

	amplitude = Math.max(amplitude, frames * SHAKE_PER_FRAME);
	screenShake ||= screen;
	for (const body of victim) if (body && !body.destroyed) shaken.set(body, 1);
	if (attacker && !attacker.destroyed && attacker.GetType() === Box2D.b2_dynamicBody && !shaken.has(attacker)) shaken.set(attacker, ATTACKER_SHAKE);
	if (victim.length) sideways = touchingGround(victim);
	return true;
};

export const holdFrame = () => {
	if (holdFrames > 0) {
		holdFrames--;
		return true;
	}
	if (cooldown > 0 && --cooldown === 0) granted = 0;
	return false;
};

const offsets = new Map();
const nudge = (display, dx, dy) => {
	if (!display || display.destroyed) return;
	const offset = offsets.get(display);
	if (offset) {
		offset.dx += dx;
		offset.dy += dy;
	} else {
		offsets.set(display, { x: display.x, y: display.y, dx, dy });
	}
};

// Draws one held frame. Called by the game loop right after holdFrame() returned true.
export const drawHeldFrame = () => {
	const done = totalFrames - holdFrames;
	const strength = (holdFrames + 1) / totalFrames;
	const side = holdFrames % 2 ? 1 : -1;
	const shake = amplitude * strength / game.editor.cameraHolder.scale.x;
	let shakeX = side * shake;
	let shakeY = 0;
	if (!sideways) {
		const angle = Math.random() * Math.PI;
		shakeX = side * Math.cos(angle) * shake;
		shakeY = side * Math.sin(angle) * shake;
	}

	const creep = CREEP * done / totalFrames * Settings.physicsTimeStep * game.editor.editorSettingsObject.gameSpeed * Settings.PTM;
	for (let body = game.world.GetBodyList(); getPointer(body) !== getPointer(NULL); body = body.GetNext()) {
		if (body.GetType() !== Box2D.b2_dynamicBody || !body.IsAwake() || body.isPortalGhost || body.isPortalShadow) continue;
		const velocity = body.GetLinearVelocity();
		const dx = velocity.x * creep, dy = velocity.y * creep;
		nudge(body.myTexture, dx, dy);
		nudge(body.mySprite, dx, dy);
	}
	for (const [body, weight] of shaken) {
		nudge(body.myTexture, shakeX * weight, shakeY * weight);
		nudge(body.mySprite, shakeX * weight, shakeY * weight);
	}

	for (const [display, offset] of offsets) display.position.set(offset.x + offset.dx, offset.y + offset.dy);
	const stage = game.stage, stageX = stage.x, stageY = stage.y;
	if (screenShake) stage.position.set(stageX + side * amplitude * strength, stageY + (Math.random() * 2 - 1) * amplitude * strength);

	game.app.render();

	stage.position.set(stageX, stageY);
	for (const [display, offset] of offsets) if (!display.destroyed) display.position.set(offset.x, offset.y);
	offsets.clear();
};

export const resetHitstop = () => {
	holdFrames = 0;
	totalFrames = 0;
	cooldown = 0;
	granted = 0;
	amplitude = 0;
	screenShake = false;
	shaken.clear();
};
